import { FC } from 'react';
import { Button } from '@components/atoms/Button';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { NotificationsActive } from '@mui/icons-material';
import { SuperAdminNotificationRecipient } from '@models/superAdminNotificationRecipient.model';
import formatDate from '@utils/formatDate';

interface SuperAdminNotificationRecipientsListProps {
  recipients: SuperAdminNotificationRecipient[];
  isLoading: boolean;
  togglingId: string | null;
  onEdit: (recipient: SuperAdminNotificationRecipient) => void;
  onDelete: (recipient: SuperAdminNotificationRecipient) => void;
  onToggleActive: (recipient: SuperAdminNotificationRecipient) => void;
}

const SuperAdminNotificationRecipientsList: FC<SuperAdminNotificationRecipientsListProps> = ({
  recipients,
  isLoading,
  togglingId,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 dark:border-indigo-400" />
      </div>
    );
  }

  if (recipients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <NotificationsActive
          className="text-gray-400 dark:text-gray-500 mb-4"
          sx={{ fontSize: 64 }}
        />
        <p className="text-gray-500 dark:text-gray-400 text-lg">No notification recipients yet.</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
          Add one to start receiving super-admin notifications.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto shadow-md rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Notes
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Active
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Added
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {recipients.map((recipient) => (
            <tr
              key={recipient.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                {recipient.email}
              </td>
              <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate">
                {recipient.notes || '—'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="flex items-center justify-center">
                  <ToggleSwitch
                    leftLabel=""
                    rightLabel=""
                    checked={recipient.active}
                    disabled={togglingId === recipient.id}
                    onChange={() => onToggleActive(recipient)}
                  />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                {formatDate(recipient.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="tertiary" size="md" onClick={() => onEdit(recipient)}>
                    Edit
                  </Button>
                  <Button
                    variant="tertiary"
                    size="md"
                    onClick={() => onDelete(recipient)}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-end px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Showing {recipients.length}{' '}
          {recipients.length === 1 ? 'recipient' : 'recipients'}
        </span>
      </div>
    </div>
  );
};

export default SuperAdminNotificationRecipientsList;
